import { API_BASE } from "@/lib/utils";

function apiErrorMessage(data, fallback) {
  const detail = data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0];
    if (typeof first === "string") return first;
    if (first?.msg) return String(first.msg);
  }
  return fallback;
}

function authHeaders(json = false) {
  const headers = {};
  if (json) headers["Content-Type"] = "application/json";
  try {
    const raw = localStorage.getItem("aivoryems.auth.session");
    const token = raw ? JSON.parse(raw)?.token : "";
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    /* no-op */
  }
  return headers;
}

/** Resolve relative /uploads paths to absolute for preview. */
export function resolveMediaUrl(url) {
  const u = String(url || "").trim();
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://") || u.startsWith("data:")) return u;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  if (u.startsWith("/")) return `${origin}${u}`;
  return `${API_BASE.replace(/\/$/, "")}/${u.replace(/^\//, "")}`;
}

export async function fetchMediaQuota() {
  const res = await fetch(`${API_BASE}/api/admin/media/quota`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Failed to load quota (${res.status})`);
  const data = await res.json();
  return normalizeMediaQuota(data) || normalizeMediaQuota(data.quota);
}

export async function listMedia({ kind = "", q = "", page = 1, pageSize = 48 } = {}) {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (kind) params.set("kind", kind);
  if (q) params.set("q", q);
  const res = await fetch(`${API_BASE}/api/admin/media?${params}`, { headers: authHeaders() });
  if (!res.ok) {
    const detail = (await res.json().catch(() => ({}))).detail || res.statusText;
    throw new Error(detail);
  }
  return res.json();
}

export async function uploadMediaFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_BASE}/api/admin/media/upload`, {
    method: "POST",
    headers: authHeaders(),
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(apiErrorMessage(data, `Upload failed (${res.status})`));
  return data;
}

export async function deleteMediaAsset(assetId) {
  const res = await fetch(`${API_BASE}/api/admin/media/${assetId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(apiErrorMessage(data, `Delete failed (${res.status})`));
  }
  return res.json();
}

export function formatBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** Normalize quota payloads from /admin/media/quota and list responses. */
export function normalizeMediaQuota(raw) {
  if (!raw || typeof raw !== "object") return null;
  const usedBytes = Number(raw.usedBytes ?? 0);
  const limitBytes = Number(raw.limitBytes ?? raw.quotaBytes ?? 0);
  const usedPct =
    raw.usedPct != null && Number.isFinite(Number(raw.usedPct))
      ? Number(raw.usedPct)
      : limitBytes > 0
        ? (usedBytes / limitBytes) * 100
        : 0;
  return {
    plan: raw.plan || "studio",
    usedBytes,
    limitBytes: limitBytes > 0 ? limitBytes : 2 * 1024 * 1024 * 1024,
    usedPct: Math.min(100, Math.max(0, usedPct)),
  };
}
